// src/utils/errorToastBus.js
// Minimal pub/sub so kailux.js's axios interceptor (which has no React
// context) can trigger a global toast without importing React or reaching
// into component state directly. One listener in practice (ErrorToast),
// but supports more for future flexibility (e.g. a second surface in an
// embedded/kiosk view).
//
// WHY THIS EXISTS: see main.py's Sep 2026 global error-sanitization block
// (backend) — every 500+ response now carries a safe message + a short
// "KL-XXXXXX" reference instead of a leaked internal error. This bus is the
// frontend half: surfaces that reference to the person as a small toast so
// a screenshot of it is enough for KaiLux to find the matching Railway/
// Sentry entry, without ever showing them the raw exception.

let _listeners = []

export function subscribeToErrorToasts(fn) {
  _listeners.push(fn)
  return () => {
    _listeners = _listeners.filter((l) => l !== fn)
  }
}

/**
 * @param {Object} toast
 * @param {string} toast.message   Short, human-readable, non-technical.
 * @param {string} [toast.errorId] "KL-XXXXXX" from the backend, or a
 *                                 client-generated "CL-XXXXXX" fallback
 *                                 when the backend never responded at all
 *                                 (network failure / CORS / timeout).
 */
export function emitErrorToast(toast) {
  const withId = { id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, ...toast }
  _listeners.forEach((fn) => {
    try { fn(withId) } catch { /* a broken listener must not break the caller */ }
  })
  return withId
}

/** Client-side fallback reference for errors that never reached the backend
 *  (network failure, CORS, DNS, timeout) — so there's still something to
 *  screenshot and quote even when the server never got a chance to mint a
 *  KL- id of its own. Distinct prefix makes it obvious which side minted it. */
export function makeClientErrorId() {
  const chars = '0123456789ABCDEF'
  let s = ''
  for (let i = 0; i < 6; i++) s += chars[Math.floor(Math.random() * 16)]
  return `CL-${s}`
}
